import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, Shield, AlertCircle, Mail, ArrowLeft, KeyRound } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../lib/apiUrl';
import { PLAN_PRICING } from '../lib/pricing';

const planDetails = {
  standard: { name: 'Standard Pack', price: PLAN_PRICING.standard.amount, assessments: 3, users: 1 },
  pro: { name: 'Pro Pack', price: PLAN_PRICING.pro.amount, assessments: 5, users: 3 }
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState('plan'); // plan → email → otp → done
  const [plan, setPlan] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const details = planDetails[plan] || planDetails.standard;

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      setError('Please fill in all fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/payment/send-activation-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), plan })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send OTP');
      toast.success('OTP sent to your email!');
      setStep('otp');
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/payment/activate-with-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp: otp.trim(), plan })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Activation failed');

      // Auto-login with the returned token
      if (data.token && data.user) {
        login(data.token, data.user);
      }
      toast.success('Subscription activated!');
      setStep('done');
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/payment/send-activation-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), plan })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to resend OTP');
      toast.success('New OTP sent!');
      setOtp('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="px-6 md:px-12 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-blue-600" />
            <span className="font-bold text-xl text-slate-900">AIVORA Comply360</span>
          </div>
        </div>
      </nav>

      <div className="px-6 md:px-12 py-8">
        <div className="max-w-lg mx-auto">
          <AnimatePresence mode="wait">

            {/* Step 1: Select Plan */}
            {step === 'plan' && (
              <motion.div key="plan" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="activate-title">
                    Activate Your Account
                  </h1>
                  <p className="text-slate-600 text-sm">
                    Completed your payment? Select your purchased plan below to activate your account.
                  </p>
                </div>

                <div className="space-y-3">
                  {['standard', 'pro'].map((p) => (
                    <button
                      key={p}
                      onClick={() => { setPlan(p); setStep('email'); setError(''); }}
                      data-testid={`select-${p}-btn`}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all hover:border-blue-500 hover:shadow-md ${plan === p ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-slate-900">{planDetails[p].name}</div>
                          <div className="text-sm text-slate-500">{planDetails[p].assessments} Assessments, {planDetails[p].users} Users</div>
                        </div>
                        <div className="font-bold text-slate-900">{planDetails[p].price}</div>
                      </div>
                    </button>
                  ))}
                </div>

                <p className="text-center text-xs text-slate-400 mt-6">
                  Haven&apos;t paid yet? <a href="/choose-plan" className="text-blue-600 hover:underline">Choose a plan first</a>
                </p>
              </motion.div>
            )}

            {/* Step 2: Enter Email */}
            {step === 'email' && (
              <motion.div key="email" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-2xl shadow-xl p-8">
                <button onClick={() => { setStep('plan'); setError(''); }} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </button>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <Mail className="h-8 w-8 text-blue-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">Verify Your Identity</h1>
                  <p className="text-slate-600 text-sm">
                    Activating <strong>{details.name}</strong> — enter the email you used for payment.
                  </p>
                </div>

                <form onSubmit={handleSendOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                    <Input type="text" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="activation-name-input" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <Input type="email" placeholder="your@email.com" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} data-testid="activation-email-input" required />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="activation-error">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                    </div>
                  )}

                  <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6" data-testid="send-otp-btn">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending OTP...</> : <>Send Verification OTP<ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </form>
              </motion.div>
            )}

            {/* Step 3: Enter OTP */}
            {step === 'otp' && (
              <motion.div key="otp" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-2xl shadow-xl p-8">
                <button onClick={() => { setStep('email'); setError(''); setOtp(''); }} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </button>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                    <KeyRound className="h-8 w-8 text-amber-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">Enter OTP</h1>
                  <p className="text-slate-600 text-sm">
                    We sent a 6-digit code to <strong>{email}</strong>
                  </p>
                </div>

                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                    className="text-center text-2xl tracking-[0.3em] py-6"
                    maxLength={6}
                    data-testid="activation-otp-input"
                    autoFocus
                  />

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="otp-error">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                    </div>
                  )}

                  <Button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white py-6" data-testid="verify-otp-btn">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Activating...</> : <>Verify & Activate<ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </form>

                <button onClick={handleResendOTP} disabled={loading} className="w-full text-center text-sm text-blue-600 hover:underline mt-4" data-testid="resend-otp-btn">
                  Didn&apos;t receive it? Resend OTP
                </button>
              </motion.div>
            )}

            {/* Step 4: Done */}
            {step === 'done' && (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="activation-success-title">
                  Account Activated!
                </h1>
                <p className="text-slate-600 mb-6">
                  Your <strong>{details.name}</strong> is now active.<br />
                  You&apos;re all set to start your ISO 27001 assessments.
                </p>

                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="font-medium text-slate-900">{details.name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Assessments</span><span className="font-medium text-slate-900">{details.assessments}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Users</span><span className="font-medium text-slate-900">{details.users}</span></div>
                  </div>
                </div>

                <Button onClick={() => navigate('/dashboard')} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6" data-testid="go-to-dashboard-btn">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            )}

          </AnimatePresence>

          <p className="text-center text-sm text-slate-500 mt-6">
            Need help? Contact <a href="mailto:support@aivora-techlabs.com" className="text-blue-600 hover:underline">support@aivora-techlabs.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
